import { useLayoutEffect, useState, useCallback, useRef } from "react";
import { Action } from "./Action";
import { LinkItem } from "./LinkItem";
import { RouteItem } from "./RouteItem";

/**
 * Hook that resolves the item name by measuring text in a hidden element
 * before the first paint, eliminating the flash of overflowing text.
 *
 * @param item - The action/link/route item with name and optional nameBreakable
 * @param containerElement - The container element to measure against
 * @returns Object with resolved name and a ref callback for the hidden measurement span
 */
export const useResolveItemNameWithHiddenMeasure = (
  item: Action | LinkItem | RouteItem,
  containerElement: HTMLElement | null | undefined
): { name: string; measureRef: (el: HTMLSpanElement | null) => void } => {
  // Default to breakable name if available (safe choice that won't overflow)
  const [resolvedName, setResolvedName] = useState<string>(
    item.nameBreakable ?? item.name
  );
  const [measureElement, setMeasureElement] = useState<HTMLSpanElement | null>(
    null
  );

  // Track the last container width to avoid unnecessary re-evaluations
  const lastContainerWidthRef = useRef<number | null>(null);

  const measureRef = useCallback((el: HTMLSpanElement | null) => {
    setMeasureElement(el);
  }, []);

  // Use useLayoutEffect to measure before paint, eliminating the flash
  useLayoutEffect(() => {
    if (!containerElement || !measureElement) return;

    const measure = () => {
      const containerWidth = containerElement.clientWidth;

      // Only re-evaluate if container width changed significantly
      if (
        lastContainerWidthRef.current !== null &&
        Math.abs(containerWidth - lastContainerWidthRef.current) <= 1
      ) {
        return;
      }

      lastContainerWidthRef.current = containerWidth;

      // Measure the normal name width
      measureElement.textContent = item.name;
      const nameWidth = measureElement.scrollWidth;

      // If normal name fits, use it; otherwise use breakable
      if (nameWidth <= containerWidth) {
        setResolvedName(item.name);
      } else {
        setResolvedName(item.nameBreakable ?? item.name);
      }
    };

    // Measure immediately
    measure();

    // Also measure on next frame to catch late font/layout changes
    const rafId = window.requestAnimationFrame(measure);

    // Re-measure on window resize
    const onResize = () => {
      lastContainerWidthRef.current = null;
      measure();
    };
    window.addEventListener("resize", onResize);

    // Re-measure when container size changes
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(containerElement);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [containerElement, measureElement, item.name, item.nameBreakable]);

  return { name: resolvedName, measureRef };
};

/**
 * Variant that uses a percentage threshold for overflow detection.
 * Similar to useResolveItemNameOverflowPct but with hidden pre-measurement.
 */
export const useResolveItemNameWithHiddenMeasurePct = (
  item: Action | LinkItem | RouteItem,
  containerElement: HTMLElement | null | undefined,
  pct?: number
): { name: string; measureRef: (el: HTMLSpanElement | null) => void } => {
  const [resolvedName, setResolvedName] = useState<string>(
    item.nameBreakable ?? item.name
  );
  const [measureElement, setMeasureElement] = useState<HTMLSpanElement | null>(
    null
  );
  const lastContainerWidthRef = useRef<number | null>(null);

  const measureRef = useCallback((el: HTMLSpanElement | null) => {
    setMeasureElement(el);
  }, []);

  useLayoutEffect(() => {
    if (!containerElement || !measureElement) return;

    const measure = () => {
      const containerWidth = containerElement.clientWidth;

      if (
        lastContainerWidthRef.current !== null &&
        Math.abs(containerWidth - lastContainerWidthRef.current) <= 1
      ) {
        return;
      }

      lastContainerWidthRef.current = containerWidth;

      measureElement.textContent = item.name;
      const nameWidth = measureElement.scrollWidth;

      // Check if overflowing or if the ratio exceeds the threshold
      let isOverflowing: boolean;
      if (nameWidth > containerWidth) {
        isOverflowing = true;
      } else {
        const ratio = containerWidth / nameWidth;
        isOverflowing = ratio > (pct ?? 1);
      }

      if (!isOverflowing) {
        setResolvedName(item.name);
      } else {
        setResolvedName(item.nameBreakable ?? item.name);
      }
    };

    measure();
    const rafId = window.requestAnimationFrame(measure);

    const onResize = () => {
      lastContainerWidthRef.current = null;
      measure();
    };
    window.addEventListener("resize", onResize);

    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(containerElement);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [containerElement, measureElement, item.name, item.nameBreakable, pct]);

  return { name: resolvedName, measureRef };
};
